import { toast } from "sonner";

import type { ApiErrorBody } from "./types";
import { ApiError } from "./client";

const STATUS_MESSAGES: Record<number, string> = {
  400: "Requisicao invalida.",
  401: "Sessao expirada. Faca login novamente.",
  403: "Voce nao tem permissao para essa acao.",
  404: "Recurso nao encontrado.",
  409: "Conflito de dados. Tente recarregar.",
  422: "Dados invalidos.",
  500: "Erro interno. Tente novamente em instantes.",
  503: "Servico indisponivel.",
};

// Prefixos comuns adicionados pelo Pydantic v2 que a gente prefere
// remover para mensagem mais limpa no toast.
const PYDANTIC_PREFIXES = [
  /^Value error,?\s+/i,
  /^Assertion error,?\s+/i,
  /^Type error,?\s+/i,
];

interface ValidationDetail {
  loc?: (string | number)[];
  msg?: string;
  type?: string;
  ctx?: { error?: string | unknown };
}

function cleanPydanticMsg(raw: string): string {
  let msg = raw;
  for (const re of PYDANTIC_PREFIXES) {
    msg = msg.replace(re, "");
  }
  return msg.trim();
}

function fieldNameFromLoc(loc: (string | number)[] | undefined): string | null {
  if (!loc || loc.length === 0) return null;
  // FastAPI normalmente prefixa com "body"/"query"/"path"; pegamos o
  // ultimo segmento string como o nome do campo.
  for (let i = loc.length - 1; i >= 0; i -= 1) {
    const part = loc[i];
    if (typeof part === "string" && part !== "body" && part !== "query" && part !== "path") {
      return part;
    }
  }
  return null;
}

function describeValidationError(details: ValidationDetail[]): string | null {
  if (!Array.isArray(details) || details.length === 0) return null;

  const lines: string[] = [];
  for (const item of details.slice(0, 4)) {
    const ctxError =
      typeof item.ctx?.error === "string" ? item.ctx.error : undefined;
    const raw = ctxError ?? item.msg ?? "";
    if (!raw) continue;
    const msg = cleanPydanticMsg(raw);
    const field = fieldNameFromLoc(item.loc);
    // Se a mensagem ja menciona o nome do campo, nao prefixa de novo.
    if (field && !msg.toLowerCase().includes(field.toLowerCase())) {
      lines.push(`${field}: ${msg}`);
    } else {
      lines.push(msg);
    }
  }
  if (lines.length === 0) return null;
  if (details.length > 4) {
    lines.push(`(+${details.length - 4} erro(s) adicional(is))`);
  }
  return lines.join("\n");
}

export function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as ApiErrorBody | null;
    const envelope = body && "error" in body ? body.error : null;

    // Erro de validacao: extrai mensagens dos `details` com nome do
    // campo. Resultado tipico:
    //   "slug deve conter apenas a-z, 0-9 e hifen (2-120 chars)"
    if (envelope?.code === "validation_error" && Array.isArray(envelope.details)) {
      const detailed = describeValidationError(
        envelope.details as ValidationDetail[],
      );
      if (detailed) return detailed;
    }

    // Outros erros do backend ja vem com message em pt-BR.
    if (envelope?.message) return envelope.message;

    // Fallback: alguns endpoints retornam { detail: "..." } cru (sem
    // passar pelos error handlers globais).
    const detail = (body as { detail?: unknown } | null)?.detail;
    if (typeof detail === "string") return detail;

    return STATUS_MESSAGES[err.status] ?? `Erro ${err.status}`;
  }
  if (err instanceof Error) return err.message;
  return "Erro desconhecido";
}

export function toastApiError(err: unknown): void {
  // Em 401, o cliente HTTP normalmente faz redirect para /login. Mas
  // se ja estamos em /login (ex.: 401 no proprio fluxo de login),
  // mostramos o toast para nao ficar silencioso.
  if (err instanceof ApiError && err.status === 401) {
    if (
      typeof window !== "undefined" &&
      window.location.pathname.startsWith("/login")
    ) {
      toast.error(describeError(err));
    }
    return;
  }
  toast.error(describeError(err));
}
